import { AxiosResponse } from 'axios';

/**
 * Wrap any data value in an AxiosResponse-shaped object so that
 * existing consumers using `response.data` keep working.
 * Replaces the per-service `asResponse` / `formatAsAxios` functions.
 */
export function asResponse<T>(data: T): AxiosResponse<T> {
  return { data } as AxiosResponse<T>;
}

/**
 * Calculate Supabase `.range(from, to)` params from page + pageSize.
 * Both `from` and `to` are inclusive (Supabase convention).
 */
export function paginationRange(page: number, pageSize: number): [number, number] {
  const from = (page - 1) * pageSize;
  return [from, from + pageSize - 1];
}

/**
 * Build standard pagination metadata used by useApiFetch consumers.
 */
export function buildPaginatedResponse<T>(
  data: T[],
  totalCount: number,
  page: number,
  pageSize: number,
) {
  const lastPage = Math.max(1, Math.ceil(totalCount / pageSize));
  return {
    data,
    count: totalCount,
    currentPage: page,
    nextPage: page < lastPage ? page + 1 : null,
    prevPage: page > 1 ? page - 1 : null,
    lastPage,
  };
}

/**
 * Extract data from a Supabase result or throw the error.
 * Use after queries where you want to fail fast on errors.
 */
export function throwIfError<T>(result: { data: T | null; error: any }): T {
  if (result.error) throw result.error;
  return result.data as T;
}
